import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCms } from '../context/CmsContext';

export default function MediaMentions() {
  const { siteSettings, mediaMentions } = useCms();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Kilde-specifikke styling/ikoner
  const getSourceMeta = (sourceName = '') => {
    const s = sourceName.toLowerCase();
    if (s.includes('dsb')) {
      return {
        icon: 'fa-solid fa-train text-[#BA141A]',
        badgeBg: 'bg-red-50 text-red-900 border-red-200',
        label: 'DSB Ejendomme'
      };
    }
    if (s.includes('linkedin')) {
      return {
        icon: 'fa-brands fa-linkedin text-[#0A66C2]',
        badgeBg: 'bg-blue-50 text-blue-900 border-blue-200',
        label: 'LinkedIn'
      };
    }
    if (s.includes('smv')) {
      return {
        icon: 'fa-solid fa-newspaper text-brand-green',
        badgeBg: 'bg-emerald-50 text-emerald-900 border-emerald-200',
        label: 'SMVdanmark'
      };
    }
    return {
      icon: 'fa-solid fa-arrow-up-right-from-square text-zinc-500',
      badgeBg: 'bg-zinc-100 text-zinc-800 border-zinc-200',
      label: sourceName || 'Medieomtale'
    };
  };

  return (
    <>
      <Helmet>
        <title>ECOLASER i medierne | Artikler og medieomtale</title>
        <meta
          name="description"
          content="Læs artikler og medieomtaler om ECOLASER. Få indblik i vores arbejde, projekter og erfaringer gennem eksterne medier."
        />
        <meta property="og:title" content="ECOLASER i medierne | Artikler og medieomtale" />
        <meta
          property="og:description"
          content="Læs artikler og medieomtaler om ECOLASER. Få indblik i vores arbejde, projekter og erfaringer gennem eksterne medier."
        />
        <meta property="og:url" content="https://ecolaser.dk/medieomtale" />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/ecolaser-hvid-original-2026-rez.png"
        />
        <link rel="canonical" href="https://ecolaser.dk/medieomtale" />
        <html lang="da" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-44 pb-24 md:pt-52 md:pb-32 overflow-hidden bg-zinc-950">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/imgi-94-billede-med-blur-scaled.jpg?width=1600&quality=80&format=auto"
            alt="ECOLASER i medierne baggrund"
            className="w-full h-full object-cover opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-zinc-950/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-left flex flex-col items-start gap-4">
          <div className="inline-flex items-center gap-2 bg-brand-green/20 border border-brand-green/30 px-4 py-1.5 rounded-full text-brand-green text-xs font-semibold uppercase tracking-wider">
            <i className="fa-solid fa-bullhorn" /> Presse & medieomtale
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
            ECOLASER i medierne
          </h1>

          <p className="text-zinc-200 text-sm sm:text-base md:text-lg max-w-3xl leading-relaxed mt-2">
            ECOLASER har gennem tiden medvirket i forskellige artikler og medieindslag. Her har vi samlet et udvalg af omtaler, hvor du kan læse mere om vores arbejde, projekter og virksomheden bag ECOLASER.
          </p>
        </div>
      </section>

      {/* Breadcrumb Navigation */}
      <nav aria-label="Brødkrummesti" className="bg-zinc-50 border-b border-zinc-200 py-3 text-xs text-zinc-500">
        <div className="max-w-7xl mx-auto px-6 flex items-center gap-2">
          <Link to="/" className="hover:text-brand-green transition-colors">
            Forside
          </Link>
          <span className="text-zinc-300">/</span>
          <span className="text-zinc-900 font-medium">ECOLASER i medierne</span>
        </div>
      </nav>

      {/* Media Mentions Cards Grid */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          {/* Introduktion til eksterne omtaler */}
          <div className="mb-12 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-900 tracking-tight">
              Artikler og eksterne omtaler
            </h2>
            <p className="text-zinc-600 text-sm md:text-base mt-2 leading-relaxed">
              Herunder finder du direkte henvisninger til eksterne medier og faglige indlæg, der belyser vores metoder og samarbejder.
            </p>
          </div>

          {/* Responsive grid til artiklerne */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {mediaMentions && mediaMentions.map((item, index) => {
              const title = typeof item.title === 'object' ? item.title?.da : item.title;
              const description = typeof item.description === 'object' ? item.description?.da : item.description;
              const source = item.source || 'Ekstern kilde';
              const buttonText = (typeof item.button_text === 'object' ? item.button_text?.da : item.button_text) || 'Læs mere';
              const url = item.url;
              const sourceMeta = getSourceMeta(source);

              return (
                <article
                  key={item.id || index}
                  className="bg-white rounded-2xl border border-zinc-200 hover:border-zinc-300 p-8 sm:p-10 flex flex-col justify-between shadow-xs hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="flex flex-col gap-5">
                    {/* Kildeangivelse / Badge */}
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <span
                        className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide border ${sourceMeta.badgeBg}`}
                      >
                        <i className={sourceMeta.icon} aria-hidden="true" />
                        <span>Kilde: {source}</span>
                      </span>

                      <span className="text-xs text-zinc-400 font-medium flex items-center gap-1.5">
                        <i className="fa-regular fa-arrow-up-right-from-square text-[11px]" aria-hidden="true" />
                        Ekstern omtale
                      </span>
                    </div>

                    {/* Overskrift */}
                    <h3 className="text-xl sm:text-2xl font-bold text-zinc-900 group-hover:text-brand-green transition-colors leading-snug">
                      {title}
                    </h3>

                    {/* Beskrivende tekst */}
                    <p className="text-zinc-600 text-sm sm:text-base leading-relaxed">
                      {description}
                    </p>
                  </div>

                  {/* Bundsektion med CTA-knap */}
                  <div className="mt-8 pt-6 border-t border-zinc-100 flex items-center justify-between">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2.5 bg-brand-green hover:bg-brand-green-light text-white font-bold px-6 py-3.5 rounded-lg text-xs sm:text-sm uppercase tracking-wider transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2"
                      aria-label={`${buttonText} (åbner i et nyt vindue)`}
                    >
                      <span>{buttonText}</span>
                      <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
                    </a>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Supplerende information til presse og samarbejdspartnere */}
          <div className="mt-16 bg-zinc-50 border border-zinc-200 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-2xl space-y-2">
              <h3 className="text-lg md:text-xl font-bold text-zinc-900">
                Spørgsmål til vores projekter eller ønske om udtalelse?
              </h3>
              <p className="text-sm text-zinc-600 leading-relaxed">
                Repræsentere du et medie, eller ønsker du yderligere oplysninger om vores kemifri lasermetoder på fredede ejendomme eller industriopgaver, er du altid velkommen til at kontakte os.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0 w-full md:w-auto">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white font-bold px-6 py-3.5 rounded-lg text-xs uppercase tracking-wider transition-colors text-center"
              >
                <i className="fa-solid fa-envelope" />
                Kontakt ECOLASER
              </Link>
              <a
                href={siteSettings.phoneTel}
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-zinc-100 border border-zinc-200 text-zinc-800 font-bold px-6 py-3.5 rounded-lg text-xs uppercase tracking-wider transition-colors text-center"
              >
                <i className="fa-solid fa-phone text-brand-green" />
                Tlf: {siteSettings.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
